import React, { useState, useEffect } from 'react';
import PartnerSidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import {
  collection,
  onSnapshot,
  query,
  where,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';

function PartnerManagers() {
  const { currentUser } = useAuth();
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus] = useState('all');
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: '',
    role: 'employee',
    status: 'active'
  });

  useEffect(() => {
    if (!currentUser) return;

    const q = query(
      collection(db, 'managers'),
      where('partnerId', '==', currentUser.uid)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const managersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setManagers(managersData);
      setIsLoading(false);
    }, (error) => {
      console.error("Error fetching managers:", error);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddManager = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await addDoc(collection(db, 'managers'), {
        ...formData,
        partnerId: currentUser.uid,
        createdAt: serverTimestamp(),
        // Defaults
        profileImageUrl: '',
        teamSize: 0,
        projects: 0,
        specialties: []
      });
      setIsModalOpen(false);
      resetForm();
      alert("Manager added successfully!");
    } catch (error) {
      console.error("Error adding manager:", error);
      alert("Failed to add manager");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteManager = async (id) => {
    if (window.confirm("Are you sure you want to delete this manager?")) {
      try {
        await deleteDoc(doc(db, 'managers', id));
      } catch (error) {
        console.error("Error deleting manager:", error);
        alert("Failed to delete manager");
      }
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      department: '',
      role: 'employee',
      status: 'active'
    });
  }

  const filteredManagers = managers.filter(manager => {
    const matchesSearch = (manager.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (manager.email?.toLowerCase() || '').includes(searchTerm.toLowerCase());

    // Note: 'position' field not always present in form, using role or department as proxy or custom field
    // Assuming 'department' is main filter
    const matchesDepartment = filterDepartment === 'all' || manager.department === filterDepartment;
    const matchesStatus = filterStatus === 'all' || (manager.status?.toLowerCase() || '') === filterStatus.toLowerCase();

    return matchesSearch && matchesDepartment && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch ((status || '').toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'on leave': return 'bg-yellow-100 text-yellow-700';
      case 'inactive': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const departments = [...new Set(managers.map(mgr => mgr.department).filter(Boolean))];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <PartnerSidebar />
      <div
        className="flex-1 overflow-y-auto transition-all duration-300"
        style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}
      >
        <div className="p-6 min-h-screen">
          <div className="flex justify-between items-center mb-6">
            <div>
              <div className="flex items-center">
                {isMobile && (
                  <button
                    onClick={toggleMobileSidebar}
                    className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  </button>
                )}
                <h1 className="text-3xl font-bold text-brand-dark">Partner Team</h1>
              </div>
              <p className="text-gray-600 mt-1">Manage your team leaders and employees</p>
            </div>
            <button
              onClick={() => { resetForm(); setIsModalOpen(true); }}
              className="bg-brand-golden hover:bg-yellow-600 text-black px-4 py-2 rounded-lg font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Member
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Members</p>
                  <h3 className="text-2xl font-bold text-brand-dark mt-1">{managers.length}</h3>
                </div>
                {/* Icon */}
              </div>
            </div>
            {/* Add more stats if needed */}
          </div>

          {/* Search and Filters */}
          <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search managers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
                />
                {/* Icon */}
              </div>
              <select
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-golden focus:border-transparent"
              >
                <option value="all">All Departments</option>
                {departments.map(dept => (
                  <option key={dept} value={dept}>{dept}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Managers List */}
          <div className="bg-brand-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Name</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Department</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Phone</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredManagers.map((manager) => (
                    <tr
                      key={manager.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setSelectedManager(manager)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-4">
                          {manager.profileImageUrl ? (
                            <img src={manager.profileImageUrl} alt={manager.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold">
                              {manager.name?.charAt(0)}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-brand-dark">{manager.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-gray-600">{manager.email}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-brand-dark">{manager.department}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-brand-dark">{manager.phone}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(manager.status)}`}>
                          {manager.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setSelectedManager(manager); }}
                            className="text-brand-golden hover:text-yellow-600 font-medium"
                          >
                            View
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteManager(manager.id); }}
                            className="text-red-500 hover:text-red-700 font-medium"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredManagers.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <p className="text-gray-500">No managers found matching your criteria</p>
              </div>
            )}
            {isLoading && (
              <div className="text-center py-8">Loading...</div>
            )}
          </div>
        </div>
      </div>

      {/* Add Manager Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full p-6">
            <h2 className="text-xl font-bold mb-4">Add Team Member</h2>
            <form onSubmit={handleAddManager} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Department</label>
                <input type="text" name="department" value={formData.department} onChange={handleInputChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select name="status" value={formData.status} onChange={handleInputChange} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-4 py-2 text-black bg-brand-golden rounded-md hover:bg-yellow-600">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manager Details Modal */}
      {selectedManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-brand-golden to-yellow-500 p-6 text-brand-white relative">
              <button
                onClick={() => setSelectedManager(null)}
                className="absolute top-4 right-4 text-brand-white hover:bg-white hover:bg-opacity-20 rounded-full p-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center text-3xl font-bold text-gray-500">
                  {selectedManager.name?.charAt(0)}
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{selectedManager.name}</h2>
                  <p className="text-brand-white/90">{selectedManager.position || 'Employee'}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{selectedManager.email}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{selectedManager.phone || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Department</p>
                  <p className="font-medium">{selectedManager.department || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-500">Status</p>
                  <p className="font-medium">{selectedManager.status}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartnerManagers;